import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaDownload, FaSync, FaRegCopy } from "react-icons/fa";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import { GoInbox } from "react-icons/go";
import { MdWindow } from "react-icons/md";
import { PiImage } from "react-icons/pi";
import { FaFile, FaFilePdf, FaTooth } from "react-icons/fa";
import { Modal, Button, Form, InputGroup, ListGroup, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import ComTable from './ComTable';
import EnoTable from './EnoTable';
import ProcedureModel from './ProcedureModel';
import STLViewer from './STLViewer';
import { toast } from 'react-toastify';
import { MdOutlineUploadFile } from "react-icons/md";
import { useCurrency } from '../../../../../context/currencyContext';

// Check if user has full access (userId === 1)
const getUserId = () => {
  const userId = localStorage.getItem('userId');
  return userId;
};

const hasFullAccess = () => {
  return getUserId() === '1';
};

// Styles for ToothSelectorModal
const modalBodyStyle = {
  padding: "20px",
};

const toothButtonStyle = {
  width: "30px",
  height: "40px",
  margin: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #ccc",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "14px",
  padding: 0,
};

const selectedToothStyle = {
  ...toothButtonStyle,
  backgroundColor: "#004aad",
  color: "white",
  borderColor: "#004aad",
};

const requiredLabelStyle = {
  display: "block",
  marginBottom: "5px",
  fontWeight: "bold",
  color: "#000000",
};

// Simple Loader Component
const SimpleLoader = ({ message = "Uploading Files" }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 99999,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px 40px',
        textAlign: 'center',
        boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
        minWidth: '300px',
      }}>
        <div style={{
          width: '70px',
          height: '70px',
          margin: '0 auto 20px',
          position: 'relative',
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #004aad',
            borderRadius: '50%',
            animation: 'spin 1.2s linear infinite',
          }}></div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '24px',
            color: '#004aad',
          }}>
            📁
          </div>
        </div>
        <div style={{
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#004aad',
          marginBottom: '10px',
        }}>
          {message}
        </div>
        <div style={{
          fontSize: '14px',
          color: '#666',
          marginBottom: '20px',
        }}>
          Please wait while we process your files...
        </div>
        <div style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#f3f3f3',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#004aad',
            animation: 'progress 2s ease-in-out infinite',
          }}></div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes progress {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

// Procedure Selector Modal
const ProcedureSelectorModal = ({
  show,
  handleClose,
  onSelect,
  allProcedures,
  accountId,
}) => {
  const { formatAmount } = useCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const frequentlyUsedProcedureIds = ["1093", "1035"];
  const canViewPricing = hasFullAccess();

  const filteredProcedures = React.useMemo(() => {
    if (!searchTerm) {
      return allProcedures.filter((proc) => !accountId || proc.accountId === accountId);
    }
    return allProcedures.filter(
      (proc) =>
        proc.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!accountId || proc.accountId === accountId)
    );
  }, [searchTerm, allProcedures, accountId]);

  const frequentlyUsed = filteredProcedures.filter((p) =>
    frequentlyUsedProcedureIds.includes(p.procedureId)
  );
  const generalProcedures = filteredProcedures.filter(
    (p) => !frequentlyUsedProcedureIds.includes(p.procedureId) && p.value
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          backgroundColor: "#004aad",
          color: "white",
          borderBottom: "none",
        }}
      >
        <Modal.Title>Select Procedure</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "1rem", borderBottom: "1px solid #dee2e6" }}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "1rem" }}>
          {frequentlyUsed.length > 0 && (
            <>
              <h5>Frequently Used</h5>
              <ListGroup variant="flush">
                {frequentlyUsed.map((proc) => (
                  <ListGroup.Item
                    key={`freq-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                    }}
                  >
                    {proc.label.replace(/\(ID:.*\)/, "").trim()}
                    {canViewPricing && ` (${formatAmount(proc.price || 0)})`}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <hr />
            </>
          )}
          {generalProcedures.length > 0 && (
            <>
              <h5>General Procedures</h5>
              <ListGroup variant="flush">
                {generalProcedures.map((proc) => (
                  <ListGroup.Item
                    key={`gen-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                    }}
                  >
                    {proc.label.replace(/\(ID:.*\)/, "").trim()}
                    {canViewPricing && ` (${formatAmount(proc.price || 0)})`}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          {frequentlyUsed.length === 0 && generalProcedures.length === 0 && (
            <p>No procedures available for this account.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// Tooth Selector Modal
const ToothSelectorModal = ({ show, handleClose, currentTeeth, onSave }) => {
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  useEffect(() => {
    if (currentTeeth) {
      setSelectedTeeth(
        currentTeeth
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t)
      );
    }
  }, [currentTeeth]);

  const toggleTooth = (toothNumber) => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.includes(toothNumber.toString())
        ? prevSelected.filter((t) => t !== toothNumber.toString())
        : [...prevSelected, toothNumber.toString()]
    );
  };

  const toggleGroup = (teethArray) => {
    const teeth = teethArray.map((tooth) => tooth.toString());
    setSelectedTeeth((prevSelected) => {
      const allSelected = teeth.every((tooth) => prevSelected.includes(tooth));
      if (allSelected) {
        return prevSelected.filter((t) => !teeth.includes(t));
      } else {
        const newSelection = [...prevSelected];
        teeth.forEach((tooth) => {
          if (!newSelection.includes(tooth)) {
            newSelection.push(tooth);
          }
        });
        return newSelection;
      }
    });
  };

  const handleDone = () => {
    onSave(
      selectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).join(", ")
    );
    handleClose();
  };

  const renderToothButton = (toothNumber) => (
    <button
      key={toothNumber}
      onClick={() => toggleTooth(toothNumber)}
      className="tooth-button"
      style={
        selectedTeeth.includes(toothNumber.toString())
          ? selectedToothStyle
          : toothButtonStyle
      }
    >
      {toothNumber}
    </button>
  );

  const teethLayout = {
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: "#004aad", color: "white" }}
      >
        <Modal.Title>Tooth Selector</Modal.Title>
      </Modal.Header>
      <Modal.Body style={modalBodyStyle}>
        <div className="text-center mb-3">
          <h5
            style={{ cursor: "pointer", display: "inline-block" }}
            onClick={() =>
              toggleGroup([...teethLayout.upperRight, ...teethLayout.upperLeft])
            }
          >
            Upper Arch
          </h5>
        </div>
        <div className="mb-4">
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.upperRight)}
                >
                  Upper Right
                </th>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.upperLeft)}
                >
                  Upper Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.upperRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
                {teethLayout.upperLeft.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <hr />
        <div className="text-center mb-3">
          <h5
            style={{ cursor: "pointer", display: "inline-block" }}
            onClick={() =>
              toggleGroup([...teethLayout.lowerRight, ...teethLayout.lowerLeft])
            }
          >
            Lower Arch
          </h5>
        </div>
        <div>
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.lowerRight)}
                >
                  Lower Right
                </th>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.lowerLeft)}
                >
                  Lower Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.lowerRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
                {teethLayout.lowerLeft.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleDone}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
          }}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditProcedureModal = ({
  show,
  handleClose,
  procedure,
  caseId,
  account: accountProp,
  onProcedureUpdated,
}) => {
  const { formatAmount } = useCurrency();
  const canViewPricing = hasFullAccess();
  
  const [editedProcedure, setEditedProcedure] = useState({
    procedure: {
      label: "Select Procedure",
      value: "",
      price: 0,
      procedureId: "",
      accountId: "",
    },
    teeth: "",
    shade: "",
    alloyMaterial: "",
    contactsEmbrasures: "",
    occlusalContact: "",
    insufficientRoom: "",
    retention: "",
    marginPosition: "",
    _id: "",
  });

  const [procedureOptions, setProcedureOptions] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showProcedureSelectorModal, setShowProcedureSelectorModal] = useState(false);
  const [showToothSelectorModal, setShowToothSelectorModal] = useState(false);
  const [caseAccountId, setCaseAccountId] = useState(null);

  useEffect(() => {
    const fetchCaseAccount = async () => {
      if (!caseId || !show) return;
      try {
        const { data } = await axios.get(
          `https://uk.backend.hms.hiradentallab.com/api/cases/cases/${caseId}`,
          { withCredentials: true }
        );
        setCaseAccountId(data.account_holder ?? accountProp ?? null);
      } catch (e) {
        console.error("Failed to fetch case account:", e);
        toast.error("Could not determine account");
        setCaseAccountId(accountProp ?? null);
      }
    };
    fetchCaseAccount();
  }, [caseId, accountProp, show]);

  useEffect(() => {
    const fetchProcedures = async () => {
      if (!caseAccountId) {
        setProcedureOptions([
          { label: "Select a procedure...", value: "", price: 0, procedureId: "", accountId: "" },
        ]);
        setPricesLoading(false);
        return;
      }

      try {
        setPricesLoading(true);
        setFetchError(null);

        const url = `https://uk.backend.hms.hiradentallab.com/api/procedures/account-specific?accountId=${caseAccountId}`;
        const { data } = await axios.get(url, { withCredentials: true });

        const formatted = Array.isArray(data.data)
          ? data.data.map((p) => ({
              label: `${p.name} (ID: ${p.procedureId})${canViewPricing ? ` - ${formatAmount(p.price)}` : ''}`,
              value: p._id,
              price: p.price,
              procedureId: p.procedureId,
              accountId: caseAccountId,
            }))
          : [];

        setProcedureOptions([
          { label: "Select a procedure...", value: "", price: 0, procedureId: "", accountId: "" },
          ...formatted,
        ]);
      } catch (e) {
        console.error("Error fetching procedures:", e);
        setFetchError(`Failed to load procedures: ${e.message}`);
        toast.error(`Failed to load procedures: ${e.message}`);
        setProcedureOptions([
          { label: "Select a procedure...", value: "", price: 0, procedureId: "", accountId: "" },
        ]);
      } finally {
        setPricesLoading(false);
      }
    };

    if (show && caseAccountId) fetchProcedures();
  }, [caseAccountId, show, canViewPricing, formatAmount]);

  useEffect(() => {
    if (!procedure || !show || pricesLoading) return;

    const base = {
      procedure: {
        label: procedure.procedure || "Select Procedure",
        value: procedure.procedureId || "",
        price: procedure.price || 0,
        procedureId: procedure.procedureId || "",
        accountId: caseAccountId ?? "",
      },
      teeth: procedure.teeth || "",
      shade: procedure.shade || "",
      alloyMaterial: procedure.alloyMaterial || procedure.alley || "",
      contactsEmbrasures: procedure.contactsEmbrasures || procedure.contacts || "",
      occlusalContact: procedure.occlusalContact || procedure.occlusion || "",
      insufficientRoom: procedure.insufficientRoom || "",
      retention: procedure.retention || "",
      marginPosition: procedure.marginPosition || procedure.margin || "",
      _id: procedure._id || "",
    };

    setEditedProcedure(base);

    if (procedureOptions.length > 1) {
      const match = procedureOptions.find((p) => p.value === procedure.procedureId);
      if (match) {
        setEditedProcedure((prev) => ({
          ...prev,
          procedure: {
            label: match.label,
            value: match.value,
            price: match.price,
            procedureId: match.procedureId,
            accountId: match.accountId,
          },
        }));
      }
    }
  }, [procedure, show, pricesLoading, procedureOptions, caseAccountId]);

  const handleProcedureSelect = (procedureId) => {
    const selected = procedureOptions.find((p) => p.value === procedureId);
    if (selected) {
      setEditedProcedure((prev) => ({
        ...prev,
        procedure: {
          label: selected.label,
          value: selected.value,
          price: selected.price,
          procedureId: selected.procedureId,
          accountId: selected.accountId,
        },
      }));
    }
    setShowProcedureSelectorModal(false);
  };

  const handleSaveTeeth = (teeth) => {
    setEditedProcedure((prev) => ({ ...prev, teeth }));
    setShowToothSelectorModal(false);
  };

  const handleChange = (name, value) => {
    setEditedProcedure((prev) => ({ ...prev, [name]: value }));
  };

  const onSave = async () => {
    const missing = ["procedure", "teeth", "shade"].filter((field) =>
      field === "procedure"
        ? !editedProcedure.procedure.value
        : !editedProcedure[field]?.trim()
    );

    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    const payload = {
      customProcedureId: editedProcedure.procedure.procedureId,
      teeth: editedProcedure.teeth,
      shade: editedProcedure.shade,
      alloyMaterial: editedProcedure.alloyMaterial,
      contactsEmbrasures: editedProcedure.contactsEmbrasures,
      occlusalContact: editedProcedure.occlusalContact,
      insufficientRoom: editedProcedure.insufficientRoom,
      retention: editedProcedure.retention,
      marginPosition: editedProcedure.marginPosition,
      price: editedProcedure.procedure.price,
      unitCount: null,
    };

    try {
      const url = `https://uk.backend.hms.hiradentallab.com/api/cases/${caseId}/procedures/${editedProcedure._id}`;
      const res = await axios.put(url, payload, { withCredentials: true });

      toast.success("Procedure updated successfully!");
      onProcedureUpdated?.(res.data.data);
      handleClose();
    } catch (err) {
      console.error("Update failed:", err);
      toast.error(err.response?.data?.message || "Failed to update procedure");
    }
  };

  const alloyOptions = ["", "Zirconia", "Emax", "PFM", "Gold", "Composite"];
  const contactOptions = ["", "Normal", "Broad", "Open", "Closed", "Light"];
  const occlusalOptions = ["", "In Occlusion", "Out of Occlusion", "Light Contact"];
  const roomOptions = ["", "Yes", "No", "Minimal Prep Required"];
  const retentionOptions = ["", "Good", "Fair", "Poor", "Mechanical", "Adhesive"];
  const marginOptions = ["", "Supragingival", "Equigingival", "Subgingival"];

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton style={{ backgroundColor: "#004aad", color: "white" }}>
        <Modal.Title>Edit Procedure</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ overflowY: "auto", maxHeight: "70vh" }}>
        <Form>
          <Form.Group as={Row} className="mb-3" controlId="formProcedure">
            <Form.Label column sm={2} style={requiredLabelStyle}>
              Procedure <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Col sm={8}>
              {pricesLoading ? (
                <div className="text-muted">Loading procedures...</div>
              ) : fetchError ? (
                <div className="text-danger">{fetchError}</div>
              ) : procedureOptions.length <= 1 ? (
                <div className="text-muted">
                  No procedures for account: <strong>{caseAccountId || "N/A"}</strong>
                </div>
              ) : (
                <InputGroup style={{ width: "125%" }}>
                  <Form.Select
                    value={editedProcedure.procedure.value}
                    onChange={(e) => handleProcedureSelect(e.target.value)}
                    style={{ cursor: "pointer" }}
                  >
                    {procedureOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label.replace(/\(ID:.*\)/, "").trim()}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowProcedureSelectorModal(true)}
                    style={{
                      backgroundColor: "#004aad",
                      borderColor: "#004aad",
                      color: "#fff",
                    }}
                    disabled={procedureOptions.length <= 1}
                  >
                    <FaRegCopy />
                  </Button>
                </InputGroup>
              )}
            </Col>
          </Form.Group>

          {editedProcedure.procedure.value && (
            <>
              <Form.Group as={Row} className="mb-3" controlId="formTeeth">
                <Form.Label column sm={2} style={requiredLabelStyle}>
                  Teeth <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col sm={10}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={editedProcedure.teeth}
                      readOnly
                      onClick={() => setShowToothSelectorModal(true)}
                      placeholder="Select Teeth"
                      style={{
                        cursor: "pointer",
                        borderColor: "#f4f4f4",
                        boxShadow: "0 0 5px rgba(0,74,173,0.3)",
                        borderRadius: "8px 0 0 8px",
                      }}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowToothSelectorModal(true)}
                      style={{
                        backgroundColor: "#004aad",
                        borderColor: "#004aad",
                        borderRadius: "0 8px 8px 0",
                      }}
                    >
                      <FaTooth style={{ color: "white" }} />
                    </Button>
                  </InputGroup>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="formShade">
                <Form.Label column sm={2} style={requiredLabelStyle}>
                  Shade <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Col sm={10}>
                  <Form.Control
                    type="text"
                    value={editedProcedure.shade}
                    onChange={(e) => handleChange("shade", e.target.value)}
                    placeholder="Enter shade"
                    style={{
                      borderColor: "#f4f4f4",
                      boxShadow: "0 0 5px rgba(0,74,173,0.3)",
                      borderRadius: "8px",
                    }}
                  />
                </Col>
              </Form.Group>

              {[
                { label: "Alloy / Material", name: "alloyMaterial", options: alloyOptions },
                { label: "Contacts / Embrasures", name: "contactsEmbrasures", options: contactOptions },
                { label: "Occlusal Contact", name: "occlusalContact", options: occlusalOptions },
                { label: "Insufficient Room", name: "insufficientRoom", options: roomOptions },
                { label: "Retention", name: "retention", options: retentionOptions },
                { label: "Margin Position", name: "marginPosition", options: marginOptions },
              ].map((field) => (
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId={`form${field.name}`}
                  key={field.name}
                >
                  <Form.Label column sm={2} style={{ fontWeight: 600, color: "#333" }}>
                    {field.label}
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Select
                      value={editedProcedure[field.name]}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      style={{
                        borderColor: "#f4f4f4",
                        boxShadow: "0 0 5px rgba(0,74,173,0.3)",
                        borderRadius: "8px",
                      }}
                    >
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt || "—"}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Form.Group>
              ))}
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSave}
          style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
          disabled={
            pricesLoading ||
            !editedProcedure.procedure.value ||
            !editedProcedure.teeth ||
            !editedProcedure.shade
          }
        >
          Save Changes
        </Button>
      </Modal.Footer>

      <ProcedureSelectorModal
        show={showProcedureSelectorModal}
        handleClose={() => setShowProcedureSelectorModal(false)}
        onSelect={handleProcedureSelect}
        allProcedures={procedureOptions}
        accountId={caseAccountId}
      />
      <ToothSelectorModal
        show={showToothSelectorModal}
        handleClose={() => setShowToothSelectorModal(false)}
        currentTeeth={editedProcedure.teeth}
        onSave={handleSaveTeeth}
      />
    </Modal>
  );
};

// CaseTable Component
const CaseTable = ({ account = '' }) => {
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [stlFiles, setStlFiles] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [showImage, setShowImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const [procedurePrices, setProcedurePrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const uploadTimerRef = useRef(null);

  const API_BASE_URL = 'https://uk.backend.hms.hiradentallab.com';
  const canViewPricing = hasFullAccess();

  const allowedExtensions = [
    '.png', '.jpg', '.jpeg', '.jfif', '.txt',
    '.stl', '.pdf', '.exe',
    '.ply', '.zip', '.doc', '.docx', '.xls', '.xlsx', '.dcm'
  ];

  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, "0") : null;
  };

  const goToInvoiceIfExists = (caseObj) => {
    const formatted = formatInvoiceNumber(caseObj?.invoiceNumber);
    if (!formatted) return;
    navigate(`/invoices/${formatted}`, {
      state: { openEditModal: true },
    });
  };

  const getImageUrl = (path) => {
    if (!path) return '/placeholder.png';
    const filename = path.split(/[\\/]/).pop();
    return `${API_BASE_URL}/uploads/${filename}`;
  };

  const showNotification = (title, message, type = 'success') => {
    const existingNotifications = document.querySelectorAll('.custom-notification');
    existingNotifications.forEach(n => n.remove());

    const notificationDiv = document.createElement('div');
    notificationDiv.className = 'custom-notification';
    notificationDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${type === 'success' ? '#28a745' : '#dc3545'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 99998;
      font-weight: 500;
      animation: slideIn 0.3s ease-out;
      max-width: 400px;
      display: flex;
      align-items: center;
    `;
    
    const icon = document.createElement('div');
    icon.style.cssText = `
      font-size: 24px;
      margin-right: 12px;
    `;
    icon.textContent = type === 'success' ? '✓' : '✗';
    
    const contentDiv = document.createElement('div');
    
    const titleEl = document.createElement('div');
    titleEl.style.cssText = `
      font-weight: bold;
      font-size: 16px;
      margin-bottom: 4px;
    `;
    titleEl.textContent = title;
    
    const messageEl = document.createElement('div');
    messageEl.style.cssText = `
      font-size: 14px;
      opacity: 0.9;
    `;
    messageEl.textContent = message;
    
    contentDiv.appendChild(titleEl);
    contentDiv.appendChild(messageEl);
    
    notificationDiv.appendChild(icon);
    notificationDiv.appendChild(contentDiv);
    
    document.body.appendChild(notificationDiv);
    
    const styleId = 'notification-animation-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    setTimeout(() => {
      notificationDiv.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        if (notificationDiv.parentNode) {
          notificationDiv.parentNode.removeChild(notificationDiv);
        }
      }, 300);
    }, 3000);
  };

  const ensureMinimumLoaderTime = async (uploadPromise, fileCount) => {
    const startTime = Date.now();
    setUploadStartTime(startTime);
    setUploading(true);

    try {
      const result = await uploadPromise;
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      return result;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      throw error;
    } finally {
      if (uploadTimerRef.current) {
        clearTimeout(uploadTimerRef.current);
      }
      setUploading(false);
      setUploadStartTime(null);
    }
  };

  useEffect(() => {
    return () => {
      if (uploadTimerRef.current) {
        clearTimeout(uploadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchProcedurePrices = async () => {
      try {
        setPricesLoading(true);
        const url = account
          ? `${API_BASE_URL}/api/procedures?accountId=${account}`
          : `${API_BASE_URL}/api/procedures`;
        const response = await axios.get(url, { withCredentials: true });
        setProcedurePrices(response.data);
      } catch (error) {
        console.error('Error fetching procedure prices:', error);
        showNotification('Error', 'Failed to load procedure prices', 'error');
      } finally {
        setPricesLoading(false);
      }
    };

    fetchProcedurePrices();
  }, [account]);

  const getProcedurePrice = (procedure) => {
    if (!canViewPricing) return '';
    const storedPrice = procedure.price || 0;
    const formattedPrice = formatAmount(storedPrice);
    return formattedPrice;
  };

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];
    if (id) {
      setCaseId(id);
      fetchCaseDetails(id);
    }
  }, []);

  const fetchCaseDetails = async (id) => {
    try {
      setLoading(true);
      const caseResponse = await axios.get(`${API_BASE_URL}/api/cases/cases/${id}`, {
        withCredentials: true,
      });
      
      setCaseData(caseResponse.data);
      if (caseResponse.data.attachedFiles && Array.isArray(caseResponse.data.attachedFiles)) {
        const imageFiles = caseResponse.data.attachedFiles.filter(file =>
          file.path && /\.(png|jpg|jpeg|jfif)$/i.test(file.path)
        );
        const stlFiles = caseResponse.data.attachedFiles.filter(file =>
          file.path && /\.stl$/i.test(file.path)
        );
        const otherFiles = caseResponse.data.attachedFiles.filter(file =>
          file.path && !/\.(png|jpg|jpeg|jfif|stl)$/i.test(file.path)
        );

        setImages(imageFiles);
        setStlFiles(stlFiles);
        setOtherFiles(otherFiles);
      } else {
        setImages([]);
        setStlFiles([]);
        setOtherFiles([]);
      }

      return caseResponse.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching case details');
      console.error('Error fetching case details:', err);
      showNotification('Error', 'Failed to fetch case details', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchCaseDetails(caseId);
    }
  }, [caseId]);

  const toggleRowExpansion = (procId) => {
    setExpandedRows(prev => ({
      ...prev,
      [procId]: !prev[procId]
    }));
  };

  const handleOpenModal = (fileUrl) => {
    setSelectedFile(fileUrl);
    setShowModal(true);
  };
const handleFileChange = async (event) => {
  const files = event.target.files;
  if (!files || files.length === 0 || !caseId) return;

  // Check number of files
  if (files.length > 30) {
    showNotification('Error', 'Maximum 30 files allowed per upload', 'error');
    event.target.value = '';
    return;
  }

  // Check individual file sizes
  for (let i = 0; i < files.length; i++) {
    if (files[i].size > 1 * 1024 * 1024 * 1024) { // 1GB
      showNotification('Error', `File "${files[i].name}" exceeds 1GB limit`, 'error');
      event.target.value = '';
      return;
    }
  }

  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('files', files[i]); // Make sure field name is 'files'
  }

  // ⭐ Log the URL for debugging
  const uploadUrl = `${API_BASE_URL}/api/cases/${caseId}/upload`;
  console.log('Uploading to:', uploadUrl);
  console.log('Number of files:', files.length);

  try {
    const response = await axios.post(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      timeout: 30 * 60 * 1000, // 30 minutes
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      },
    });

    console.log('Upload response:', response.data);

    if (response.data.success) {
      showNotification(
        'Upload Successful!',
        `${files.length} file${files.length > 1 ? 's' : ''} uploaded successfully!`
      );
      await fetchCaseDetails(caseId);
    } else {
      showNotification(
        'Upload Failed',
        response.data.message || 'Upload failed',
        'error'
      );
    }
  } catch (error) {
    console.error('Upload error:', error);
    console.error('Error response:', error.response?.data);
    
    let errorMessage = 'Upload failed';

    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Upload endpoint not found. Please check server configuration.';
      } else if (error.response.status === 413) {
        errorMessage = 'File too large (max 1GB each)';
      } else if (error.response.status === 500) {
        errorMessage = error.response.data?.message || 'Server error during upload';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = 'Upload timed out. File may be too large.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    showNotification('Upload Failed', errorMessage, 'error');
  } finally {
    event.target.value = '';
  }
};
  const handleDelete = async (fileId) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/cases/${caseId}/files/${fileId}`, { withCredentials: true });
        await fetchCaseDetails(caseId);
        showNotification('Success', 'File deleted successfully!');
      } catch (err) {
        console.error("Delete failed:", err);
        showNotification(
          'Error',
          `Failed to delete file: ${err.response?.data?.message || err.message}`,
          'error'
        );
      }
    }
  };
const handleReplace = async (fileId, file, fileType) => {
  if (!file || !caseId || !fileId) {
    showNotification('Error', 'Invalid file or case information', 'error');
    return;
  }

  let validExtensions;
  if (fileType === 'image') {
    validExtensions = ['.png', '.jpg', '.jpeg', '.jfif'];
  } else if (fileType === 'stl') {
    validExtensions = ['.stl'];
  } else {
    validExtensions = allowedExtensions;
  }

  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

  if (!validExtensions.includes(ext)) {
    showNotification(
      'Invalid File Type',
      `Please upload ${validExtensions.join(', ')} files.`,
      'error'
    );
    return;
  }

  if (file.size > 1 * 1024 * 1024 * 1024) { // 1GB
    showNotification('File Too Large', 'File size exceeds 1GB limit', 'error');
    return;
  }

  const formData = new FormData();
  formData.append('file', file);

  const replacePromise = axios.put(
    `${API_BASE_URL}/api/cases/cases/${caseId}/files/${fileId}/replace`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true,
      timeout: 30 * 60 * 1000, // 30 minutes timeout
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }
  );

  try {
    const response = await ensureMinimumLoaderTime(replacePromise, 1);

    if (response.data.success) {
      showNotification('Success', 'File replaced successfully!');
      await fetchCaseDetails(caseId);
    } else {
      throw new Error(response.data.message || "Failed to replace file");
    }
  } catch (error) {
    console.error("Error replacing file:", error);
    let errorMessage = "Failed to replace file";

    if (error.response) {
      if (error.response.status === 413) {
        errorMessage = "File too large. Maximum 1GB allowed.";
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = "Request timed out. Please try again.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    showNotification('Replace Failed', errorMessage, 'error');
  }
};

  const togglePrivacy = async (id, currentStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/files/${id}/privacy`, { private: !currentStatus }, { withCredentials: true });

      setImages(prev => prev.map(file =>
        file._id === id ? { ...file, private: !currentStatus } : file
      ));
      setStlFiles(prev => prev.map(file =>
        file._id === id ? { ...file, private: !currentStatus } : file
      ));
      setOtherFiles(prev => prev.map(file =>
        file._id === id ? { ...file, private: !currentStatus } : file
      ));
      
      showNotification('Success', `File marked as ${!currentStatus ? 'private' : 'public'}`);
    } catch (err) {
      console.error("Privacy toggle failed:", err);
      showNotification('Error', 'Failed to toggle privacy', 'error');
    }
  };

  const handleImageDownload = async (file) => {
    try {
      const response = await fetch(getImageUrl(file.path), {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name || `file_${Date.now()}`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      showNotification('Download Started', 'File download has started');
    } catch (error) {
      console.error('File download failed:', error);
      showNotification('Error', 'Failed to download file', 'error');
    }
  };

  const handleDeleteOther = async (id) => {
    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/cases/${caseId}/files/${id}`, { withCredentials: true });
        await fetchCaseDetails(caseId);
        showNotification('Success', 'File deleted successfully!');
      } catch (error) {
        console.error('Delete error:', error);
        showNotification(
          'Error',
          `Delete failed: ${error.response?.data?.message || error.message}`,
          'error'
        );
      }
    }
  };

  const handleProcedureUpdated = async () => {
    const updatedCase = await fetchCaseDetails(caseId);
    showNotification('Success', 'Procedure updated successfully!');
    goToInvoiceIfExists(updatedCase);
  };

  const handleDeleteProcedure = async (procedureId) => {
    if (window.confirm("Are you sure you want to permanently delete this procedure?")) {
      setDeleting(true);
      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/cases/${caseId}/procedures/${procedureId}`,
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data.success) {
          setCaseData((prev) => ({
            ...prev,
            procedures: prev.procedures.filter((p) => p._id !== procedureId),
          }));
          showNotification('Success', 'Procedure deleted successfully!');
          goToInvoiceIfExists(caseData);
        }
      } catch (err) {
        console.error('Error deleting procedure:', err);
        let errorMessage = 'Failed to delete procedure';
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = err.response.data?.message || 'Procedure not found';
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        }
        showNotification('Error', errorMessage, 'error');
      } finally {
        setDeleting(false);
      }
    }
  };

  const toggleDropdown = (procId, event) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === procId ? null : procId);
  };

  const countTeeth = (teethString) => {
    if (!teethString || typeof teethString !== 'string' || teethString.trim() === '') {
      return 1;
    }

    const teethList = teethString.split(',').map((item) => item.trim());
    let totalTeeth = 0;

    teethList.forEach((item) => {
      if (item.includes('-')) {
        const [start, end] = item
          .split('-')
          .map((num) => parseInt(num.trim(), 10))
          .filter((num) => !isNaN(num));
        if (start && end && start <= end) {
          totalTeeth += end - start + 1;
        }
      } else {
        if (!isNaN(parseInt(item, 10))) {
          totalTeeth += 1;
        }
      }
    });

    return totalTeeth > 0 ? totalTeeth : 1;
  };

  const displayValue = (value, defaultValue = '') => {
    return value && value !== defaultValue ? value : '-';
  };

  // Custom CSS for table styling - Left, Right, and Bottom borders only
  const tableStyles = `
    /* Procedure Table Styling */
    .procedure-table {
      width: 100%;
      border-collapse: collapse !important;
      border-left: 2px solid #dee2e6 !important;
      border-right: 2px solid #dee2e6 !important;
      border-bottom: 2px solid #dee2e6 !important;
    }

    /* No top border */
    .procedure-table {
      border-top: none !important;
    }

    /* Header */
    .procedure-table thead th {
      background-color: #f1f1f1 !important;
      color: #000 !important;
      border: none !important;
      padding: 12px;
      font-weight: 600;
    }

    /* Header - No hover effect */
    .procedure-table thead tr:hover th,
    .procedure-table thead th:hover {
      background-color: #f1f1f1 !important;
      color: #000 !important;
    }

    /* Body cells - No internal borders */
    .procedure-table tbody td {
      background-color: #fff;
      border: none !important;
      padding: 12px;
      transition: background-color 0.2s ease;
    }

    /* Full row hover */
    .procedure-table tbody tr:hover td {
      background-color: #f1f1f1 !important;
    }

    /* Buttons ka color change na ho */
    .procedure-table tbody tr:hover button {
      background-color: #004aad !important;
      color: #fff !important;
    }

    /* SVG icon ka color/background change na ho */
    .procedure-table tbody tr:hover svg {
      background: transparent !important;
    }

    /* Expanded row ka apna background rahe */
    .procedure-table tbody tr[style] td {
      background-color: #f8f9fa !important;
    }

    /* Bottom border for each row */
    .procedure-table tbody tr {
      border-bottom: 1px solid #dee2e6 !important;
    }

    .procedure-table tbody tr:last-child {
      border-bottom: none !important;
    }
  `;

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #004aad',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}></div>
      <p style={{ marginTop: '20px', fontSize: '18px', color: '#004aad' }}>
        Loading case details...
      </p>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
  
  if (error) return <div>Error: {error}</div>;
  if (!caseData) return <div>No case data found</div>;

  return (
    <div style={{ width: "100%" }}>
      {/* Inject Table Styles */}
      <style>{tableStyles}</style>

      {/* Simple Loader */}
      {uploading && <SimpleLoader />}

      {/* WORK REQUESTED SECTION - NO BACKGROUND */}
      <div className="row" style={{ padding: "7px", borderRadius: "8px", marginTop: '10px' }}>
        <div className="col" style={{ textAlign: "left", width: "100%" }}>
          <h2 style={{ color: '#333', margin: 0 }}>Work Requested</h2>
        </div>
        <div className="col text-end">
          <ProcedureModel
            caseId={caseId}
            account={account}
            onProcedureAdded={async () => {
              const updatedCase = await fetchCaseDetails(caseId);
              goToInvoiceIfExists(updatedCase);
            }}
          />
        </div>
      </div>

      {/* TABLE - WITH LEFT, RIGHT, AND BOTTOM BORDERS */}
      <div className="row mt-3">
        <table className="table procedure-table">
          <thead>
            <tr className="align-middle">
              <th style={{ width: "8%" }}>Units</th>
              <th>Description</th>
              {canViewPricing && <th style={{ textAlign: "right", width: "15%" }}>Price</th>}
              <th style={{ width: "10%" }}></th>
            </tr>
          </thead>
          <tbody>
            {caseData.procedures && caseData.procedures.length > 0 ? caseData.procedures.map((proc, index) => {
              const procId = proc._id || proc.key || index;
              return (
                <React.Fragment key={procId}>
                  <tr className="align-middle">
                    <td style={{ fontWeight: "500" }}>{(proc.calculatedUnits)}</td>
                    <td>
                      {proc.procedure}
                      <IoIosArrowDropdownCircle
                        onClick={() => toggleRowExpansion(procId)}
                        style={{
                          cursor: 'pointer',
                          marginLeft: '10px',
                          fontSize: '1.2em',
                          verticalAlign: 'middle',
                          transform: expandedRows[procId] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out',
                          color: '#004aad'
                        }}
                        title={expandedRows[procId] ? "Collapse" : "Expand"}
                      />
                    </td>
                    {canViewPricing && (
                      <td style={{ textAlign: "right" }}>
                        {getProcedurePrice(proc)}
                      </td>
                    )}
                    <td>
                      <div style={{ position: 'relative', display: 'inline-flex' }}>
                        <button
                          onClick={() => {
                            setCurrentProcedure(proc);
                            setShowEditModal(true);
                          }}
                          style={{
                            backgroundColor: '#004aad',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px 0 0 5px',
                            padding: '5px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '32px',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => toggleDropdown(procId, e)}
                          style={{
                            backgroundColor: '#004aad',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0 5px 5px 0',
                            padding: '5px 8px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '32px',
                          }}
                        >
                          ▼
                        </button>
                        
                        {openDropdownId === procId && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: '0',
                              marginTop: '4px',
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              minWidth: '120px',
                              overflow: 'hidden',
                            }}
                          >
                            <button
                              onClick={() => {
                                setOpenDropdownId(null);
                                handleDeleteProcedure(proc._id);
                              }}
                              disabled={deleting}
                              style={{
                                width: '100%',
                                padding: '8px 16px',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: '#dc3545',
                                cursor: deleting ? 'not-allowed' : 'pointer',
                                textAlign: 'left',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'background-color 0.2s',
                                borderTop: '1px solid #eee',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              <FaTrash size={14} />
                              {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRows[procId] && (
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <td colSpan={canViewPricing ? "4" : "3"}>
                        <div style={{ padding: '15px' }}>
                          <strong>Full Details:</strong>
                          <div className="row mt-2">
                            <div className="col-md-6">
                              <p><strong>Teeth:</strong> {displayValue(proc.teeth || proc.teethCount)}</p>
                              <p><strong>Shade:</strong> {displayValue(proc.shade)}</p>
                              {canViewPricing && <p><strong>Price:</strong> {getProcedurePrice(proc)}</p>}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            }) : (
              <tr><td colSpan={canViewPricing ? "4" : "3"} className="text-center">No procedures requested yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EditProcedureModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        procedure={currentProcedure}
        onProcedureUpdated={handleProcedureUpdated}
        account={account}
        caseId={caseId}
      />

      {/* COMPONENTS - NO BACKGROUND */}
      <div style={{ display: "flex", gap: "1%" }}>
        <div>
          <MdWindow style={{ fontSize: '3rem', textAlign: "left", marginLeft: "1px" }} />
          <strong>Components</strong> <br /><br />
        </div>
        <ComTable
          caseId={caseId}
          currentComponents={caseData?.components || []}
          onUpdate={(updatedComponents) => {
            setCaseData(prev => ({
              ...prev,
              components: updatedComponents
            }));
          }}
        />
      </div>

      {/* ENCLOSURES - NO BACKGROUND */}
      <div style={{ display: "flex", gap: "1.8%" }}>
        <div>
          <GoInbox style={{ fontSize: '2.5rem', textAlign: "left" }} />
          <strong>Enclosures</strong> <br /><br />
        </div>
        <EnoTable caseId={caseId} enclosures={caseData.enclosures || []} />
      </div>

      {/* UPLOAD CASE FILES - WITH "Upload" TEXT INSTEAD OF ICON */}
      <div className="upload-container" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px',
        padding: '10px 0',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <FaRegCopy style={{ fontSize: '2.5rem' }} />
        <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Upload Case Files</span>
        
        {/* Hidden file input */}
        <input
          type="file"
          id="hiddenFileInput"
          style={{ display: 'none' }}
          accept={allowedExtensions.join(',')}
          multiple
          onChange={handleFileChange}
        />
        
        {/* Upload Button with Text - Unique Design */}
        <button
          onClick={() => document.getElementById('hiddenFileInput').click()}
          style={{
            backgroundColor: '#004aad',
            color: 'white',
            border: 'none',
            padding: '8px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            marginLeft: 'auto',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 74, 173, 0.3)',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#003a8a';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 74, 173, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#004aad';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 74, 173, 0.3)';
          }}
        >
          ⬆ Upload
        </button>
      </div>

      {/* IMAGE FILES - NO BACKGROUND */}
      <div>
        <div style={{ backgroundColor: 'lightblue', padding: '15px', borderRadius: '5px', fontWeight: 'bold', fontSize: '19px', marginTop: '20px' }}>
          Image Files
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '10px' }}>
          {images.length > 0 ? images.map((image) => (
            <div key={image._id} style={{
              width: '180px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              boxShadow: '2px 2px 10px rgba(0,0,0,0.1)',
              padding: '8px',
              backgroundColor: 'white',
              textAlign: 'center',
              position: 'relative'
            }}>
              <img
                src={getImageUrl(image.path)}
                alt={image.name}
                onError={(e) => {
                  console.error(`Image load failed for ${image.name}: ${e.target.src}`);
                  e.target.src = '/placeholder.png';
                }}
                style={{
                  width: "100%",
                  height: "120px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
                onClick={() => setShowImage(getImageUrl(image.path))}
              />
              <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                <b>Date:</b> {new Date(image.date).toLocaleString()}
              </p>
              <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                <b>Last Updated:</b> {new Date(image.updatedAt).toLocaleString()}
              </p>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>File:</b> {image.path.split("/").pop()}
              </p>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>From:</b> Lab
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '5px' }}>
                <button
                  onClick={() => setShowImage(getImageUrl(image.path))}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "16px", color: "#007bff" }}
                >
                  <PiImage />
                </button>
                <button
                  onClick={() => handleImageDownload(image)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "#28a745" }}
                >
                  <FaDownload />
                </button>
                <button
                  onClick={() => handleDelete(image._id)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "red" }}
                >
                  <FaTrash />
                </button>
                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.png,.jpg,.jpeg';
                    input.onchange = (event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        handleReplace(image._id, file, 'image');
                      }
                    };
                    input.click();
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: "14px",
                    color: "orange"
                  }}
                  title="Replace file"
                >
                  <FaSync />
                </button>
              </div>
              <div style={{ marginTop: "10%" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={image.private || false}
                    onChange={() => togglePrivacy(image._id, image.private)}
                  />
                  Private
                </label>
              </div>
            </div>
          )) : (
            <p>No images found</p>
          )}
        </div>
        {showImage && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            }}
            onClick={(e) => { e.stopPropagation(); setShowImage(null); }}
          >
            <img
              src={showImage}
              alt="Fullscreen"
              onError={(e) => {
                console.error('Fullscreen image load failed:', showImage);
                e.target.src = '/placeholder.png';
              }}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
      </div>

      {/* INTRAORAL SCAN FILES - NO BACKGROUND */}
      <div>
        <div style={{ backgroundColor: "lightblue", padding: "15px", fontWeight: "bold", marginTop: "20px" }}>
          Intraoral Scan Files
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginTop: "10px" }}>
          {stlFiles.length > 0 ? stlFiles.map((file) => (
            <div key={file._id} style={{
              width: '180px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              boxShadow: '2px 2px 10px rgba(0,0,0,0.1)',
              padding: '8px',
              backgroundColor: 'white',
              textAlign: 'center',
              position: 'relative'
            }}>
              <STLViewer
                fileUrl={getImageUrl(file.path)}
                size="small"
              />
              <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                <b>Date:</b> {new Date(file.date).toLocaleString()}
              </p>
              <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                <b>Last Updated:</b> {new Date(file.updatedAt).toLocaleString()}
              </p>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>File:</b> {file.name}
              </p>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>From:</b> Lab
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '5px' }}>
                <button
                  onClick={() => handleOpenModal(getImageUrl(file.path))}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: "16px",
                    color: "#007bff"
                  }}
                >
                  <PiImage />
                </button>
                <button
                  onClick={() => handleImageDownload(file)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "#28a745" }}
                >
                  <FaDownload />
                </button>
                <button
                  onClick={() => handleDelete(file._id)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "red" }}
                >
                  <FaTrash />
                </button>
                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = ".stl";
                    input.onchange = (event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        handleReplace(file._id, file, 'stl');
                      }
                    };
                    input.click();
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: "14px",
                    color: "orange"
                  }}
                  title="Replace file"
                >
                  <FaSync />
                </button>
              </div>
              <div style={{ marginTop: "10%" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={file.private || false}
                    onChange={() => togglePrivacy(file._id, file.private)}
                  />
                  Private
                </label>
              </div>
            </div>
          )) : (
            <p>No STL files found</p>
          )}
        </div>

        {showModal && selectedFile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              zIndex: 9999,
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                width: "80vw",
                height: "80vh",
                backgroundColor: "#00a1d6",
                padding: "20px",
                position: "relative",
                borderRadius: "8px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowModal(false)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "red",
                  color: "white",
                  border: "none",
                  padding: "8px 15px",
                  fontSize: "16px",
                  cursor: "pointer",
                  borderRadius: "5px",
                }}
              >
                ✖
              </button>
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <STLViewer
                  fileUrl={selectedFile}
                  size="extra-large"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* OTHER FILES - NO BACKGROUND */}
      <div>
        <div style={{ backgroundColor: "lightblue", padding: "15px", fontWeight: "bold", marginTop: "20px" }}>
          Other Files
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginTop: "10px" }}>
          {otherFiles.length > 0 ? otherFiles.map((file) => (
            <div key={file._id} style={{
              width: '180px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              boxShadow: '2px 2px 10px rgba(0,0,0,0.1)',
              padding: '8px',
              backgroundColor: 'white',
              textAlign: 'center',
              position: 'relative'
            }}>
              {file.path.toLowerCase().endsWith('.pdf') ? (
                <FaFilePdf style={{ fontSize: '3rem', color: 'red' }} />
              ) : file.path.toLowerCase().endsWith('.txt') ? (
                <div style={{ fontSize: '3rem' }}>📄</div>
              ) : (
                <FaFile style={{ fontSize: '3rem' }} />
              )}
              <div style={{ marginBottom: '5px' }}>
                <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                  <b>Date:</b> {new Date(file.date).toLocaleString()}
                </p>
                <p style={{ fontSize: "12px", marginTop: "5px", color: "black" }}>
                  <b>Last Updated:</b> {new Date(file.updatedAt).toLocaleString()}
                </p>
              </div>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>File:</b> {file.name}
              </p>
              <p style={{ fontSize: "12px", color: "black" }}>
                <b>From:</b> Lab
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '5px' }}>
                <button
                  onClick={() => handleImageDownload(file)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "#28a745" }}
                >
                  <FaDownload />
                </button>
                <button
                  onClick={() => handleDeleteOther(file._id)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: "14px", color: "red" }}
                >
                  <FaTrash />
                </button>
                <button
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = allowedExtensions.join(",");
                    input.onchange = (event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        handleReplace(file._id, file, 'other');
                      }
                    };
                    input.click();
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: "14px",
                    color: "orange"
                  }}
                  title="Replace file"
                >
                  <FaSync />
                </button>
              </div>
              <div style={{ marginTop: "10px" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={file.private || false}
                    onChange={() => togglePrivacy(file._id, file.private)}
                  />
                  Private
                </label>
              </div>
            </div>
          )) : (
            <p>No files found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseTable;